import { CommonModule } from '@angular/common'
import type { AfterViewInit } from '@angular/core'
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  input,
  Input,
  Output,
  viewChild,
  ViewChild,
} from '@angular/core'
import { FormsModule } from '@angular/forms'

import { FieldBase } from '@app/modules/forms/abstracts/field-base'
import { CustomFormsModule } from '@app/modules/forms/forms.module'
import { Breakpoint2Service } from '@app/modules/ui/services/breakpoint-2.service'
import { UIModule } from '@app/modules/ui/ui.module'

@Component({
  selector: 'sb-field-message',
  templateUrl: './field-message.component.html',
  styleUrls: ['./field-message.component.scss'],
  imports: [CommonModule, FormsModule, CustomFormsModule, UIModule],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class FieldMessageComponent extends FieldBase<string | null> implements AfterViewInit {
  private readonly breakpoint2Service = inject(Breakpoint2Service)

  @ViewChild('textareaRef', { static: true })
  readonly textareaRef!: ElementRef<HTMLTextAreaElement>

  @Input()
  placeholder: string | undefined

  @Input()
  autofocus = false

  @Input()
  rows: number | undefined

  @Input()
  maxRows: number | undefined

  @Input()
  autoexpand = false

  @Input()
  sendDisabled = false

  readonly allowVoiceMessage = input.required<boolean | undefined>()

  readonly hasAnyContent = input.required<boolean>()

  @Output() readonly send = new EventEmitter<void>()
  @Output() readonly fieldMouseDown = new EventEmitter<MouseEvent>()

  @Output() readonly record = new EventEmitter<void>()

  readonly voiceMessageButton = viewChild<unknown, ElementRef<HTMLElement>>('voiceMessageButton', { read: ElementRef })

  value = ''

  get validatedMaxRows(): number | undefined {
    if (this.maxRows === undefined) {
      return undefined
    }

    if (this.rows === undefined) {
      return this.maxRows
    }
    return Math.max(this.rows, this.maxRows)
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit()
    if (this.autofocus) {
      this.textareaRef.nativeElement.focus()
    }
  }

  writeValue(value: string | null): void {
    this.value = (value ?? '').trim()
  }

  onInputChange(value: string): void {
    this.onChange(value.trim())
  }

  onBlur(): void {
    this.fieldBlur.emit()
    this.onTouched()
  }

  onSendMouseDown(event: MouseEvent) {
    if (this.breakpoint2Service.supportsHover()) {
      event.preventDefault()
    }
    this.send.emit()
  }

  onFieldMouseDown(event: MouseEvent) {
    this.fieldMouseDown.emit(event)
  }
}
